import { useState, useEffect } from 'react';
import axios from 'axios';

// Hook to fetch journal entries without expedition reference for a specific client
export const useFetchJournalWithoutExpRef = (compte = null) => {
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJournalEntries = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_API_BASE_URL}/exped/without-exp${compte ? `?compte=${compte}` : ''}`;
        const response = await axios.get(url);
        setJournalEntries(response.data.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching journal entries');
        setLoading(false);
      }
    };

    fetchJournalEntries();
  }, [compte]);

  const refetch = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/exped/without-exp${compte ? `?compte=${compte}` : ''}`;
      const response = await axios.get(url);
      setJournalEntries(response.data.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching journal entries');
      setLoading(false);
    }
  };

  return { journalEntries, loading, error, refetch };
};

// Hook to fetch journal entries with expedition reference
export const useFetchJournalWithExpRef = (compte = null) => {
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJournalEntries = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_API_BASE_URL}/exped/with-exp${compte ? `?compte=${compte}` : ''}`;
        const response = await axios.get(url);
        setJournalEntries(response.data.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching journal entries');
        setLoading(false);
      }
    };

    fetchJournalEntries();
  }, [compte]);

  const refetch = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/exped/with-exp${compte ? `?compte=${compte}` : ''}`;
      const response = await axios.get(url);
      setJournalEntries(response.data.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching journal entries');
      setLoading(false);
    }
  };

  return { journalEntries, loading, error, refetch };
};

// Hook to fetch journal entries with both devis and facture but no expedition reference
export const useFetchJournalWithBothRefs = (compte = null) => {
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJournalEntries = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_API_BASE_URL}/exped/with-both-refs${compte ? `?compte=${compte}` : ''}`;
        const response = await axios.get(url);
        setJournalEntries(response.data.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching journal entries');
        setLoading(false);
      }
    };

    fetchJournalEntries();
  }, [compte]);

  const refetch = async () => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/exped/with-both-refs${compte ? `?compte=${compte}` : ''}`;
      const response = await axios.get(url);
      setJournalEntries(response.data.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching journal entries');
      setLoading(false);
    }
  };

  return { journalEntries, loading, error, refetch };
};

// Hook to update a journal entry
export const useUpdateJournalEntry = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const updateJournalEntry = async (id, data) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/exped/${id}`, data);
      
      setSuccess(true);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating journal entry');
      setLoading(false);
      throw err;
    }
  };

  return { updateJournalEntry, loading, error, success };
};

// Hook to call maj for journal entries
export const useCreateJournalExpEntries = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  const createJournalExpEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/exped/maj`);
      
      setResult(response.data.stats);
      setSuccess(true);
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating journal entries');
      setLoading(false);
      throw err;
    }
  };

  return { createJournalExpEntries, loading, error, success, result };
};


// Hook to fetch journal entries with devis only (no facture)
export const useFetchJournalWithDevisOnly = (compte = null) => {
    const [journalEntries, setJournalEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      const fetchJournalEntries = async () => {
        try {
          setLoading(true);
          const url = `${process.env.REACT_APP_API_BASE_URL}/exped/with-devis-only${compte ? `?compte=${compte}` : ''}`;
          const response = await axios.get(url);
          setJournalEntries(response.data.data);
          setLoading(false);
        } catch (err) {
          setError(err.response?.data?.message || 'Error fetching journal entries');
          setLoading(false);
        }
      };
  
      fetchJournalEntries();
    }, [compte]);
  
    const refetch = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_API_BASE_URL}/exped/with-devis-only${compte ? `?compte=${compte}` : ''}`;
        const response = await axios.get(url);
        setJournalEntries(response.data.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching journal entries');
        setLoading(false);
      }
    };
  
    return { journalEntries, loading, error, refetch };
  };



  export const useVerifyCompte = () => {
    const [compteInfo, setCompteInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [exists, setExists] = useState(false);
  
    const verifyCompte = async (compteNumber) => {
      try {
        if (!compteNumber || compteNumber.trim() === '') {
          setError('Veuillez entrer un numéro de compte');
          setExists(false);
          return { exists: false, error: 'Veuillez entrer un numéro de compte' };
        }
  
        // Convert to uppercase to ensure consistency
        const uppercaseCompte = compteNumber.trim().toUpperCase();
        
        setLoading(true);
        setError(null);
        
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/exped/verify-compte/${uppercaseCompte}`
        );
        
        // If we get here, the request was successful
        setCompteInfo(response.data.data);
        setExists(true);
        setLoading(false);
        
        return {
          exists: true,
          compteInfo: response.data.data
        };
      } catch (err) {
        // Handle different types of errors
        if (err.response?.status === 404) {
          setError('Compte non trouvé');
          setExists(false);
        } else {
          setError(err.response?.data?.message || 'Erreur lors de la vérification du compte');
          setExists(false);
        }
        
        setCompteInfo(null);
        setLoading(false);
        
        return {
          exists: false,
          error: err.response?.data?.message || 'Erreur lors de la vérification du compte'
        };
      }
    };
  
    const resetCompteInfo = () => {
      setCompteInfo(null);
      setError(null);
      setExists(false);
    };
  
    return {
      compteInfo,
      loading,
      error,
      exists,
      verifyCompte,
      resetCompteInfo
    };
  };